import { useFormik } from "formik";
import { useAppSelector, useIconSize, usePermissions } from "../../app/hooks";
import { selectCurrentProject } from "../../app/features/project/projectSlice";
import { useTogglePublicProjectMutation, } from "../../app/features/project/projectApiSlice";
import { useState, useEffect, useMemo } from "react";
import { FiLink, FiClipboard, FiCheck } from "react-icons/fi";
import { generatePublicUrl } from "@utils/projectIdEncoder";
import TranslatableText from "../translations/TranslatableText";
import Button from "../button/Button";
import FadeAnimation from "../animations/FadeAnimation";
import Spinner from "@components/spinner/Spinner";

interface PublicProjectToggleProps {
  disabled?: boolean;
}

const PublicProjectToggle = ({ disabled = false }: PublicProjectToggleProps) => {
  const { project } = useAppSelector(selectCurrentProject);
  const { canMutateProject } = usePermissions();
  const [updateProject, { isLoading }] = useTogglePublicProjectMutation();
  const [copied, setCopied] = useState(false);
  const isDisabled = useMemo(() => disabled || isLoading || !canMutateProject, [disabled, isLoading, canMutateProject]);

  const formik = useFormik({
    initialValues: {
      is_public: project?.is_public || false
    },
    onSubmit: async (values) => {
      // No need to submit - we'll update immediately when the toggle changes
    }
  });

  // Update form when project changes
  useEffect(() => {
    formik.setFieldValue('is_public', project?.is_public || false);
  }, [project]);

  const handleToggleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isDisabled) return;
    const newValue = e.target.checked;
    formik.setFieldValue('is_public', newValue);

    if (project?.id && canMutateProject) {
      try {
        await updateProject({
          projectId: project.id,
          isPublic: newValue
        }).unwrap();
      } catch (error) {
        console.error("Failed to update project visibility:", error);
        // Revert to original value on error
        formik.setFieldValue('is_public', project?.is_public || false);
      }
    }
  };

  const publicUrl = useMemo(() => {
    if (!project?.id) return '';
    
    try {
      return generatePublicUrl(project.id, 'activities');
    } catch (error) {
      console.error('Failed to generate public URL:', error);
      // Fallback to original format if encoding fails
      return `${window.location.origin}/public/project/${project.id}/activities`;
    }
  }, [project?.id]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const iconSize = useIconSize(16);

  // Only admins can make projects public
  if (!canMutateProject) {
    return null;
  }

  return (
    <div className="d-flex f-column py-1">
      <h3 className="fs-16 ff-medium text-new-gray">
        <TranslatableText translationKey="main.public_access" />
      </h3>

      <div className="d-flex align-items-center mb-2">
        <span className="fs-14">
          <TranslatableText translationKey="main.make_project_public" />
        </span>
        <div className="form-check form-switch ps-2">
          {isLoading ? <Spinner size={16} isBlack /> :
            <input
              id="is_public"
              name="is_public"
              type="checkbox"
              className="form-check-input"
              checked={formik.values.is_public}
              onChange={handleToggleChange}
              disabled={isDisabled}
            />}
        </div>
      </div>

      <p className="fs-12 text-muted">
        <TranslatableText translationKey="main.public_project_explanation" />
      </p>

      <FadeAnimation isShown={formik.values.is_public}>
        <div className="d-flex f-column gap-2 p-1 bg-light rounded-2 mt-2">
          <h4 className="fs-14 ff-medium d-flex gap-2 align-items-center">
            <FiLink />
            <TranslatableText className="ps-1" translationKey="main.public_link" />
          </h4>

          <div className="d-flex align-items-center gap-8 pt-1">
            <input
              type="text"
              value={publicUrl}
              readOnly
              className="w-75"
              style={{ fontSize: 'var(--fs-11)' }}
            />
            <Button
              className={copied ? "bg-success" : "bg-secondary"}
              onClick={copyToClipboard}
              disabled={isLoading}
            >
              {copied ? <FiCheck size={iconSize} /> : <FiClipboard size={iconSize} />}
            </Button>
          </div>

          <p className="fs-12 text-muted mt-1 mb-0">
            <TranslatableText translationKey="main.share_public_link" />
          </p>
        </div>
      </FadeAnimation>
    </div>
  );
};

export default PublicProjectToggle; 